import { createColorTransform } from '@limonade/color-science';
import { PCEMatchAudit, type PCEMatchAuditOptions } from '@limonade/eye-exporters';
import { createPCEPaletteFrame, pcePaletteReferenceMetadata, type PCEPalettePresetId,
  type PCEOptions, type SemanticSegmentationProvider } from '@limonade/pce-core';

/** One creative-match configuration and runner for browser and embedded WebKit. */
export const PCE_MATCH_DEFAULTS = Object.freeze({
  referenceInputColorSpace: 'sRGB', sourceInputColorSpace: 'sRGB',
  workingSpace: 'OKLab-analysis-from-sRGB', quality: 'balanced' as const,
  ignoreGreenscreen: true, protectSkin: true, matchMidtoneSkin: true,
  matchTones: true, matchSixVectors: true, localDetection: true,
  lutSize: 33 as 17 | 33, auditPasses: 2,
});
export interface PCEMatchSettings {
  referencePresetId?: PCEPalettePresetId;
  referenceInputColorSpace: string; sourceInputColorSpace: string; workingSpace: string;
  quality: 'quality' | 'balanced' | 'realtime';
  ignoreGreenscreen: boolean; protectSkin: boolean; matchMidtoneSkin: boolean;
  matchTones: boolean; matchSixVectors: boolean; localDetection: boolean;
  lutSize: 17 | 33; auditPasses: number;
}
export function matchingPCEOptions(settings: PCEMatchSettings): PCEOptions {
  return {
    palettePresetId: settings.referencePresetId,
    referenceInputColorSpace: settings.referenceInputColorSpace, sourceInputColorSpace: settings.sourceInputColorSpace,
    workingColorSpace: settings.workingSpace, outputColorSpace: settings.sourceInputColorSpace,
    sampling: { quality: settings.quality }, ignoreGreenscreen: settings.ignoreGreenscreen,
    protectSkin: settings.protectSkin, matchMidtoneSkin: settings.matchMidtoneSkin,
    matchTonalRanges: settings.matchTones, matchSixVectorColors: settings.matchSixVectors, temporalStability: false,
  };
}
export function detectionSRGB(frame: ImageData, from: string): ImageData {
  if (from === 'sRGB') return frame;
  const transform = createColorTransform({ from, to: 'sRGB' });
  const data = new Uint8ClampedArray(frame.data);
  for (let i = 0; i < data.length; i += 4) {
    const rgb = transform.rgb({ r: frame.data[i]! / 255, g: frame.data[i + 1]! / 255, b: frame.data[i + 2]! / 255 });
    data[i] = rgb.r * 255; data[i + 1] = rgb.g * 255; data[i + 2] = rgb.b * 255;
  }
  return typeof ImageData !== 'undefined' ? new ImageData(data, frame.width, frame.height) : { ...frame, data } as ImageData;
}
export async function runPCEMatchPipeline(source: ImageData, reference: ImageData, settings: PCEMatchSettings,
  provider?: SemanticSegmentationProvider, options: Pick<PCEMatchAuditOptions, 'targetedMatches' | 'onProgress'> = {},
) {
  const preset = settings.referencePresetId ? pcePaletteReferenceMetadata(settings.referencePresetId) : undefined;
  if (preset) {
    reference = createPCEPaletteFrame(preset.id);
    settings = { ...settings, referenceInputColorSpace: 'sRGB' };
  }
  // Both interfaces feed local detection display-sRGB, while CMF receives the
  // original declared-domain samples. No technical-QC heuristics alter this grade.
  const detectionProvider = !preset && settings.localDetection && provider ? {
    segment: (frame: ImageData | VideoFrame) => provider.segment(detectionSRGB(frame === reference ? reference : source,
      frame === reference ? settings.referenceInputColorSpace : settings.sourceInputColorSpace)),
  } : undefined;
  const audit = await new PCEMatchAudit({ semanticSegmentationProvider: detectionProvider }).analyze({
    sourceFrame: source, referenceFrame: reference, options: matchingPCEOptions(settings),
  }, { ...options, size: settings.lutSize, maxPasses: settings.auditPasses });
  if (!preset && settings.localDetection && !provider) audit.report.warnings.push('Local detection provider unavailable; deterministic fallback used.');
  if (preset) {
    audit.report.referencePreset = preset;
    audit.report.warnings.push('Built-in palette is a global creative color target, not a corresponding scene. Score measures palette color adherence, not scene similarity.');
    if (preset.id === 'film-inspired') audit.report.warnings.push('Film-inspired is a palette target, not film emulation.');
  }
  return audit;
}
